import { redirect } from 'react-router-dom';

import { InjectAsyncReducer, PageLoaderFunction } from 'ha/types/routing';
import { getFeatureFlags } from 'ha/modules/FeatureFlags';
import { getFavoriteListings } from 'ha/modules/FavoriteListing/actions';
import { searchParamsToObject } from 'ha/utils/routes/searchParamsToObject';

import { STATE_NAME } from './constants';
import { reducer } from './reducer';
import {
  loadListing,
  loadCity,
  setQueryParams,
  loadTenantReviewsOverall,
} from './actions';
import { RootState } from './types';
import {
  getIsNotFound,
  getAdvertiser,
  getListingUnitTypeId,
} from './selectors';

export { ListingPageRoot as default } from './ListingPageRoot';

export const loader: PageLoaderFunction = ({
  params: { id = '', countryCode = '', city = '' },
  context: {
    store: { dispatch, getState },
    intl,
  },
  request,
}) => {
  const url = new URL(request.url);
  const query = searchParamsToObject(url.searchParams);
  dispatch(setQueryParams(query));

  const state = getState() as RootState;
  const { tenantMULFlow, tenantMULFlowRedirect } = getFeatureFlags(state);

  // When the id in the URL is prefixed with "ut" then we fetch the unit type
  // since denotes that this property has multiple units.
  const fetchUnitType = tenantMULFlow === 'on' && id.includes('ut');
  const propertyId = fetchUnitType ? Number(id.replace('ut', '')) : Number(id);

  return Promise.all([
    dispatch(
      loadListing(
        Number(propertyId),
        {
          ...query,
          useUnitType: fetchUnitType,
        },
        intl,
      ),
    ),
    dispatch(getFavoriteListings(fetchUnitType)),
  ]).then(() => {
    const STATE = getState() as RootState;
    const isNotFound = getIsNotFound(STATE);
    if (isNotFound) {
      return dispatch(
        loadCity({
          countryCode,
          city,
          lang: intl.lang,
        }),
      );
    }

    if (
      tenantMULFlow === 'on' &&
      tenantMULFlowRedirect === 'on' &&
      !id.startsWith('ut')
    ) {
      const unitTypeId = getListingUnitTypeId(STATE);
      //eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw redirect(
        `${url.pathname.replaceAll(`/${id}/`, `/ut${unitTypeId}/`)}${
          url.search
        }`,
        301,
      );
    }

    const advertiserId = getAdvertiser(getState()).id;
    return Promise.all([dispatch(loadTenantReviewsOverall(advertiserId))]);
  });
};

export const injectAsyncReducer: InjectAsyncReducer = store => {
  store.injectAsyncReducer(STATE_NAME, reducer);
};
